import { SaveOutlined } from '@ant-design/icons';
import { Checkbox, Col, Form, Input, InputNumber, message, Modal, Row } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import CreateBtn from '@/components/app/CreateBtn';
import BooleanTag from '@/components/app/BooleanTag';
import FareConfigurationsTariffParamsInput from '@/components/FormItems/FareConfigurationsTariffParamsInput';
import { CompanyAccountDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

const columns: IColumnType<CompanyAccountDto>[] = [
  {
    title: 'companySettingsPage_TariffParameters',
    dataIndex: 'tariffParameterName',
  },
  {
    title: 'companySettingsPage_SellOnTheGoFromKms',
    dataIndex: 'fromKms',
  },
  {
    title: 'companySettingsPage_SellOnTheGoToKms',
    dataIndex: 'toKms',
  },
  {
    title: 'companySettingsPage_SellOnTheGoTariffValue',
    dataIndex: 'tariffValue',
  },
  {
    title: 'companySettingsPage_SellOnTheGoIsInRate',
    dataIndex: 'isInRate',
    render: v => <BooleanTag value={v} />,
  },
];
const filters: Array<IFilterItem> = [{ type: 'TEXT', name: 'tariffParameterName', label: 'companySettingsPage_TariffParameters' }];

const SellOnTheGoFareConfigurationTab = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const createEntity = values => {
    setLoading(true);
    apiSdk.SellOnTheGoFareConfigurationService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.SellOnTheGoFareConfigurationService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/fleets/sell-on-the-go-fare-configurations/get-fare-configurations"
        bordered
        order_by="createdOn"
        order_by_asc={true}
        filters={filters}
        allowEdit={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
          });
        }}
        allowDelete={true}
        delete_url="/fleets/sell-on-the-go-fare-configurations/delete"
        searchBtnAlignLeft
        extraBtns={
          <CreateBtn
            onClick={() => {
              setIsNewForm(true);
              setFormVisible(true);
              form.resetFields();
            }}
          />
        }
      ></ZTable>
      {formVisible ? (
        <Modal
          visible={formVisible}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={isNewForm ? t('companySettingsPage_AddFareConfigurations') : t('companySettingsPage_UpdateFareConfigurations')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateEntity(values);
                } else {
                  createEntity(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item
                  label={t('companySettingsPage_TariffParameters')}
                  name="tariffParameterId"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <FareConfigurationsTariffParamsInput />
                </Form.Item>

                <Form.Item
                  label={t('companySettingsPage_SellOnTheGoFromKms')}
                  name="fromKms"
                  dependencies={['toKms']}
                  rules={[
                    { required: true, message: VALIDATE_REQUIRED },
                    ({ getFieldValue }) => ({
                      validator(_, fromKms) {
                        const toKms = getFieldValue('toKms');

                        if (fromKms > toKms) {
                          return Promise.reject(t('companySettingsPage_SellOnTheGoFromKmsLessThanToKms'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputNumber min={0} className="w-100" />
                </Form.Item>

                <Form.Item
                  label={t('companySettingsPage_SellOnTheGoToKms')}
                  name="toKms"
                  dependencies={['fromKms']}
                  rules={[
                    { required: true, message: VALIDATE_REQUIRED },
                    ({ getFieldValue }) => ({
                      validator(_, toKms) {
                        const fromKms = getFieldValue('fromKms');
                        if (fromKms > toKms) {
                          return Promise.reject(t('companySettingsPage_SellOnTheGoFromKmsLessThanToKms'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
                <Form.Item
                  label={t('companySettingsPage_SellOnTheGoTariffValue')}
                  name="tariffValue"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
                <Form.Item label={t('companySettingsPage_SellOnTheGoIsInRate')} valuePropName="checked" name="isInRate">
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
    </div>
  );
};
export default SellOnTheGoFareConfigurationTab;
